import { resolveApiBaseUrl } from "./apiBase";

const API_URL = resolveApiBaseUrl();

const getAuthToken = (): string | null => {
  const token = localStorage.getItem('idToken') || localStorage.getItem('authToken');
  return token ? token.trim() : null;
};

const buildHeaders = () => {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  };
};

export type EmailTemplateMeta = {
  id: string;
  name: string;
  type: 'verification' | 'completion';
  builtIn: boolean;
};

export type EmailTemplate = EmailTemplateMeta & {
  subject: string;
  html: string;
  text: string;
};

export type EmailTemplateUpdate = {
  name?: string;
  type?: 'verification' | 'completion';
  subject: string;
  html: string;
  text: string;
};

export type EmailTemplateCreate = {
  name: string;
  type: 'verification' | 'completion';
  subject: string;
  html: string;
  text: string;
};

export const fetchEmailTemplates = async (): Promise<EmailTemplateMeta[]> => {
  const url = API_URL ? `${API_URL}/email/templates` : '/email/templates';
  const response = await fetch(url, { headers: buildHeaders() });
  const text = await response.text();
  if (!response.ok) {
    throw new Error(text || 'Failed to fetch email templates');
  }
  const data = JSON.parse(text);
  if (!data.success || !Array.isArray(data.data)) {
    throw new Error('Invalid email templates response');
  }
  return data.data;
};

export const fetchEmailTemplate = async (id: string): Promise<EmailTemplate> => {
  const url = API_URL ? `${API_URL}/email/templates/${encodeURIComponent(id)}` : `/email/templates/${encodeURIComponent(id)}`;
  const response = await fetch(url, { headers: buildHeaders() });
  const text = await response.text();
  if (!response.ok) {
    throw new Error(text || 'Failed to fetch email template');
  }
  const data = JSON.parse(text);
  if (!data.success || !data.data) {
    throw new Error('Invalid email template response');
  }
  return data.data;
};

export const createEmailTemplate = async (
  payload: EmailTemplateCreate
): Promise<{ id: string; name: string; type: string }> => {
  const url = API_URL ? `${API_URL}/email/templates` : '/email/templates';
  const response = await fetch(url, {
    method: 'POST',
    headers: buildHeaders(),
    body: JSON.stringify(payload)
  });
  const text = await response.text();
  if (!response.ok) {
    throw new Error(text || 'Failed to create email template');
  }
  const data = JSON.parse(text);
  if (!data.success || !data.data) {
    throw new Error('Invalid create response');
  }
  return data.data;
};

export const updateEmailTemplate = async (
  id: string,
  payload: EmailTemplateUpdate
): Promise<{ id: string; name: string; type: string }> => {
  const url = API_URL ? `${API_URL}/email/templates/${encodeURIComponent(id)}` : `/email/templates/${encodeURIComponent(id)}`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: buildHeaders(),
    body: JSON.stringify(payload)
  });
  const text = await response.text();
  if (!response.ok) {
    throw new Error(text || 'Failed to update email template');
  }
  const data = JSON.parse(text);
  if (!data.success || !data.data) {
    throw new Error('Invalid update response');
  }
  return data.data;
};
